import {
  useClerk,
  useOrganization,
  useOrganizationList,
  useUser,
} from "@clerk/tanstack-react-start";
import { useLocation, useNavigate, useParams } from "@tanstack/react-router";
import { ChevronsUpDown, Settings } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Spinner from "@/components/ui/kibo-ui/spinner";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { useOrganizationsQuery } from "@/hooks/queries/use-organizations-query";
import { getInitials } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";

const MAX_ORG_SWITCH_LOGS = 20;
const ADMIN_NAVIGATION_STORAGE_KEY = "lynx-admin-navigation";
const ORG_NAME_DISPLAY_LIMIT = 18;

export function TeamSwitcher() {
  const { isMobile } = useSidebar();
  const { organization } = useOrganization();
  const { user } = useUser();
  const { openOrganizationProfile } = useClerk();
  const { userMemberships, setActive, isLoaded } = useOrganizationList({
    userMemberships: {
      infinite: true,
    },
  });
  const { tenantId } = useParams({ strict: false });
  const navigate = useNavigate();
  const pathname = useLocation({ select: (l) => l.pathname });
  const href = useLocation({ select: (l) => l.href });
  type SimpleOrg = { id: string; name: string; imageUrl: string | null };
  const userOrganizations = userMemberships?.data || [];
  const memberOrganizations: SimpleOrg[] = useMemo(
    () =>
      userOrganizations.map((membership) => ({
        id: membership.organization.id,
        name: membership.organization.name,
        imageUrl: membership.organization.imageUrl ?? null,
      })),
    [userOrganizations]
  );

  const { data: organizationsQueryData } = useOrganizationsQuery({
    enabled: user?.publicMetadata?.admin === true,
  });

  const adminOrganizations: SimpleOrg[] = useMemo(() => {
    if (!organizationsQueryData?.organizations) {
      return [];
    }

    return organizationsQueryData.organizations.map((org) => ({
      id: org.id,
      name: org.name,
      imageUrl: null,
    }));
  }, [organizationsQueryData?.organizations]);

  const isAdmin = user?.publicMetadata?.admin === true;

  // Fetch all organizations if user is admin
  const availableOrganizations = useMemo(() => {
    if (isAdmin) {
      if (adminOrganizations.length > 0) {
        return adminOrganizations;
      }

      return memberOrganizations;
    }

    return memberOrganizations;
  }, [adminOrganizations, isAdmin, memberOrganizations]);

  // Get current organization info for display
  const getCurrentOrgInfo = () => {
    // For admin users, always check the URL first since they might not be members
    if (isAdmin && tenantId) {
      // Find the org by tenantId from all organizations
      const currentOrg = availableOrganizations.find(
        (org) => org.id === tenantId
      );
      if (currentOrg) {
        return currentOrg;
      }
    }

    // If we have an active organization from Clerk, use it
    if (organization) {
      return organization;
    }

    // Fallback to first available organization
    return availableOrganizations[0];
  };

  const currentOrgInfo = getCurrentOrgInfo();
  const truncateOrgName = (name: string) =>
    name.length > ORG_NAME_DISPLAY_LIMIT
      ? `${name.slice(0, ORG_NAME_DISPLAY_LIMIT)}...`
      : name;
  const currentOrgDisplayName = truncateOrgName(currentOrgInfo?.name ?? "");

  // Refs to track component state and prevent race conditions
  const mountedRef = useRef(true);
  const hasAutoSetRef = useRef(false);
  const switchInProgressRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup effect to track mounted state
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Cancel any in-flight requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // If no organization is active, set the first one (only for regular users or admin users who are members)
  useEffect(() => {
    // Guard: Don't auto-set if already attempted
    if (hasAutoSetRef.current) {
      return;
    }

    if (
      !organization &&
      availableOrganizations.length > 0 &&
      isLoaded &&
      !isAdmin
    ) {
      // Mark that we've attempted auto-set
      hasAutoSetRef.current = true;

      // Only auto-set organization for non-admin users
      const firstOrgId = availableOrganizations[0].id;
      setActive({ organization: firstOrgId }).then(() => {
        // Only navigate if component is still mounted
        if (mountedRef.current) {
          navigate({
            to: "/$tenantId",
            params: { tenantId: firstOrgId },
            replace: true,
          });
        }
      });
    }

    // For admin users, don't auto-set organization - let them browse freely
  }, [
    organization,
    availableOrganizations,
    setActive,
    navigate,
    isLoaded,
    isAdmin,
  ]);

  if (!isLoaded) {
    return <Spinner />;
  }

  // For admin users, we can work without an active organization (using tenantId from URL)
  // For regular users, we need an active organization
  if (!currentOrgInfo) {
    return null;
  }

  const log = (message: string, data?: Record<string, unknown>) => {
    const logEntry = {
      timestamp: new Date().toISOString(),
      message,
      data,
    };
    console.log(message, data);

    // Save to localStorage
    const existingLogs = JSON.parse(
      localStorage.getItem("lynx-org-switch-logs") || "[]"
    ) as Record<string, unknown>[];
    existingLogs.push(logEntry);
    // Keep only last 20 logs
    if (existingLogs.length > MAX_ORG_SWITCH_LOGS) {
      existingLogs.shift();
    }
    localStorage.setItem("lynx-org-switch-logs", JSON.stringify(existingLogs));
  };

  const handleOrganizationSwitch = async (orgId: string) => {
    // Guard: Don't allow concurrent switches
    if (switchInProgressRef.current) {
      log("Organization switch already in progress, ignoring new request");
      return;
    }

    log("Switching to organization:", {
      targetOrgId: orgId,
      isAdmin,
      currentOrgId: organization?.id,
    });

    switchInProgressRef.current = true;

    try {
      if (isAdmin) {
        await handleAdminSwitch(orgId);
        return;
      }

      await switchAsMember(orgId);
    } catch (error) {
      log("Error in handleOrganizationSwitch:", {
        error: error instanceof Error ? error.message : error,
        stack: error instanceof Error ? error.stack : undefined,
      });
    } finally {
      // Only reset if component still mounted
      if (mountedRef.current) {
        switchInProgressRef.current = false;
      }
    }
  };

  const handleAdminSwitch = async (orgId: string) => {
    const isMember = userOrganizations.some(
      (membership) => membership.organization.id === orgId
    );

    if (isMember) {
      await switchAsMember(orgId);
      return;
    }

    log("Admin switching to non-member org, syncing first...");
    const syncedSuccessfully = await syncOrganization(orgId);

    if (!syncedSuccessfully) {
      return;
    }

    await navigateWithoutActivation(orgId);
  };

  const switchAsMember = async (orgId: string) => {
    await setActive({ organization: orgId });

    if (!mountedRef.current) {
      log("Component unmounted, skipping navigation");
      return;
    }

    log("Final navigation for member orgs:", { orgId });
    navigate({
      to: "/$tenantId",
      params: { tenantId: orgId },
      replace: true,
    });
    log("Navigation complete");
  };

  const syncOrganization = async (orgId: string): Promise<boolean> => {
    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch("/api/admin/sync-organization", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ organizationId: orgId }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const errorData = await response.json();
        log("Failed to sync organization:", errorData);
        return false;
      }

      log("Organization synced successfully");
      return true;
    } catch (error) {
      // Don't log if request was aborted (component unmounted)
      if (error instanceof Error && error.name === "AbortError") {
        log("Sync request aborted (component unmounted)");
        return false;
      }
      log("Failed to sync organization:", error as Record<string, unknown>);
      return false;
    } finally {
      abortControllerRef.current = null;
    }
  };

  const navigateWithoutActivation = async (orgId: string) => {
    if (!mountedRef.current) {
      log("Component unmounted, skipping navigation");
      return;
    }

    log("Navigating to org without setting active:", {
      orgId,
      currentPath: pathname,
    });

    sessionStorage.setItem(
      ADMIN_NAVIGATION_STORAGE_KEY,
      JSON.stringify({
        targetOrgId: orgId,
        timestamp: Date.now(),
        isAdmin: true,
      })
    );

    log("About to navigate, current URL:", { href });

    if (mountedRef.current) {
      await navigate({
        to: `/${orgId}`,
        replace: true,
      });
      log("Navigation complete");
    }
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton size="lg">
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                <Avatar className="size-4">
                  <AvatarImage src={currentOrgInfo.imageUrl ?? undefined} />
                  <AvatarFallback>
                    {getInitials(currentOrgInfo.name)}
                  </AvatarFallback>
                </Avatar>
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span
                  className="truncate font-semibold"
                  title={currentOrgInfo.name}
                >
                  {currentOrgDisplayName}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              Organizations {isAdmin && "(Admin View)"}
            </DropdownMenuLabel>
            {availableOrganizations.map((org) => (
              <DropdownMenuItem
                className="gap-2 p-2"
                key={org.id}
                onClick={() => {
                  log("Dropdown item clicked for org:", {
                    orgId: org.id,
                    orgName: org.name,
                  });
                  handleOrganizationSwitch(org.id);
                }}
              >
                <div className="flex size-6 items-center justify-center rounded-sm border">
                  <Avatar className="size-4 shrink-0">
                    <AvatarImage src={org.imageUrl ?? undefined} />
                    <AvatarFallback>{getInitials(org.name)}</AvatarFallback>
                  </Avatar>
                </div>
                {org.name}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            {organization && (
              <DropdownMenuItem onClick={() => openOrganizationProfile()}>
                <Settings className="mr-2 h-4 w-4" />
                Organization Settings
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
